import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Text,
} from '@byteclaw/visage';
import { GetStaticPaths, GetStaticProps } from 'next';
import Image from 'next/image';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { Facebook, Twitter } from 'react-feather';
import { IReadTimeResults } from 'reading-time';
import {
  ArticleCard,
  ArticleCardData,
  ArticleGrid,
  ArticlePublishDate,
  Button,
  Layout,
  Link,
  SEO,
  TimeToRead,
} from '../../components';
import { siteMetadata } from '../../constants';
import {
  createArticleHref,
  createAuthorHref,
  createCategoryHref,
  createSocialImageArticleHref,
} from '../../helpers/createHref';
import { ArticleJSONLD } from '../../jsonld/ArticleJSONLD';
import { mdxComponents } from '../../mdxComponents';
import { graphqlClient, imageLoader } from '../../services';
import { constructUrl } from '../../utils/contructUrl';
import { objectToGetParams } from '../../utils/objectToGetParams';
import {
  ArticleQuery,
  ArticleQueryVariables,
  ArticleDocument,
  GetAllArticlesDocument,
  GetAllArticlesQuery,
} from '../../queries.graphql';
import { serializeMdx } from '../../helpers/serializeMdx';
import { Gender } from '../../__generated__/graphqcms.types';
import { generateImageSizes } from '../../helpers/generateImageSizes';

interface ArticlePageProps {
  categories: { id: string; name: string; slug: string }[];
  articlesByAuthor: ArticleCardData[];
  articlesInCategory: ArticleCardData[];
  article: {
    author?: {
      gender: Gender;
      name: string;
      photo: { url: string };
      slug: string;
    } | null;
    createdAt: string;
    categories: { id: string; name: string; slug: string }[];
    compiledContent: MDXRemoteSerializeResult;
    excerpt: string;
    title: string;
    keywords: string[];
    publishedAt?: string | null;
    updatedAt: string;
    slug: string;
    titlePicture: {
      height?: number | null;
      width?: number | null;
      description?: string | null;
      url: string;
    };
    timeToRead: IReadTimeResults;
  };
}

export default function ArticlePage({
  article,
  articlesByAuthor,
  articlesInCategory,
  categories,
}: ArticlePageProps) {
  return (
    <Layout categories={categories} maxWidth={680}>
      <SEO
        description={article.excerpt}
        keywords={article.keywords}
        title={article.title}
        path={createArticleHref(article)}
        image={createSocialImageArticleHref('fb', {
          photoUrl: article.titlePicture.url,
          publishedAt: article.publishedAt,
        })}
        meta={[
          <meta
            property="og:url"
            key="og-url"
            content={constructUrl(
              siteMetadata.siteUrl,
              createArticleHref(article),
            )}
          />,
          <meta property="og:type" key="og-type" content="article" />,
          <meta
            property="og:published_time"
            key="og-published-time"
            // @ts-expect-error this is not nullable because we require published pages
            content={article.publishedAt}
          />,
          <meta
            property="og:modified_time"
            key="og-modified-time"
            content={article.updatedAt}
          />,
          <meta
            property="og:section"
            key="og-section"
            content={article.categories[0]?.name ?? ''}
          />,
        ]}
      />
      <ArticleJSONLD article={article} />
      <Box as="article" styles={{ mb: '2rem' }}>
        <Heading styles={{ fontSize: 4, lineHeight: '1.1em', mb: '1rem' }}>
          {article.title}
        </Heading>
        <Heading
          level={2}
          styles={{
            color: 'color(shadesText tint(30%))',
            fontSize: 1,
            fontFamily: 'body',
            lineHeight: 1,
            mb: '1rem',
          }}
        >
          {article.excerpt}
        </Heading>
        <Flex styles={{ alignItems: 'center', mb: '3rem' }}>
          <Box
            styles={{
              backgroundColor: 'color(#000 tint(50%))',
              backgroundRepeat: 'norepeat',
              backgroundSize: 'contain',
              height: 50,
              width: 50,
              borderRadius: '50%',
              overflow: 'hidden',
              position: 'relative',
              mr: 1,
            }}
          >
            <Image
              src={article.author!.photo!.url}
              layout="fill"
              objectFit="contain"
              sizes={generateImageSizes(50)}
            />
          </Box>

          <Flex styles={{ flexDirection: 'column' }}>
            <Link
              href={createAuthorHref(article.author!)}
              rel="author"
              styles={{
                borderWidth: 0,
                fontSize: -1,
                lineHeight: '1.3em',
                display: 'block',
              }}
            >
              {article.author!.name}
            </Link>
            <Text
              styles={{
                color: 'color(shadesText, tint(30%))',
                fontSize: -2,
                lineHeight: '1em',
              }}
            >
              <ArticlePublishDate
                // @ts-expect-error this is not nullable because we require published articles
                value={article.publishedAt}
              />
              {' · '}
              <TimeToRead long minutes={article.timeToRead.minutes} />
            </Text>
          </Flex>
          <Flex styles={{ ml: 'auto', position: 'relative' }}>
            <IconButton
              as={Link}
              label="Zdieľať na Facebooku"
              icon={Facebook}
              styles={{ mr: 2 }}
              stroked
              href={`https://www.facebook.com/sharer/sharer.php${objectToGetParams(
                {
                  u: constructUrl(
                    siteMetadata.siteUrl,
                    createArticleHref(article),
                  ),
                },
              )}`}
              title="Zdieľať na Facebooku"
            />
            <IconButton
              as={Link}
              label="Zdielať na Twitteri"
              icon={Twitter}
              stroked
              href={`https://twitter.com/share${objectToGetParams({
                url: constructUrl(
                  siteMetadata.siteUrl,
                  createArticleHref(article),
                ),
                text: article.title,
              })}`}
              title="Zdielať na Twitteri"
            />
          </Flex>
        </Flex>
        <Box
          as="figure"
          styles={{
            display: 'block',
            py: 0,
            px: 0,
            mx: -2,
            my: 0,
            mb: '2rem',
            textAlign: 'center',
          }}
        >
          <Image
            src={article.titlePicture.url}
            layout="responsive"
            // @ts-expect-error this is not nullable because it's an image
            width={article.titlePicture.width}
            // @ts-expect-error this is not nullable because it's an image
            height={article.titlePicture.height}
          />
          {article.titlePicture.description ? (
            <Text
              as="figcaption"
              styles={{
                fontFamily: 'body',
                fontSize: -1,
                lineHeight: '1.2em',
                py: 1,
                px: 2,
                color: 'color(shadesText tint(30%))',
              }}
            >
              {article.titlePicture.description}
            </Text>
          ) : null}
        </Box>
        <MDXRemote {...article.compiledContent} components={mdxComponents} />
      </Box>
      <Divider
        label={
          <Flex>
            <IconButton
              as={Link}
              label="Zdieľať na Facebooku"
              icon={Facebook}
              styles={{ mr: 2 }}
              stroked
              href={`https://www.facebook.com/sharer/sharer.php${objectToGetParams(
                {
                  u: constructUrl(
                    siteMetadata.siteUrl,
                    createArticleHref(article),
                  ),
                },
              )}`}
              title="Zdieľať na Facebooku"
            />
            <IconButton
              as={Link}
              label="Zdielať na Twitteri"
              icon={Twitter}
              stroked
              href={`https://twitter.com/share${objectToGetParams({
                url: constructUrl(
                  siteMetadata.siteUrl,
                  createArticleHref(article),
                ),
                text: article.title,
              })}`}
              title="Zdielať na Twitteri"
            />
          </Flex>
        }
        lineProps={{ styles: { borderBottomWidth: 2 } }}
        styles={{ mb: '2rem' }}
      />
      {articlesByAuthor.length > 0 ? (
        <Box
          as="section"
          styles={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
        >
          <Heading
            level={4}
            styles={{ mx: 0, my: 0, px: 0, py: 0, lineHeight: '1.2em' }}
          >
            {article.author!.name}{' '}
            {article.author!.gender === Gender.Male ? 'napísal' : 'napísala'}{' '}
            ďalšie zaujímavé články
          </Heading>
          <ArticleGrid>
            {articlesByAuthor.slice(0, 6).map((articleByAuthor) => (
              <ArticleCard
                compact
                key={articleByAuthor.id}
                article={articleByAuthor}
              />
            ))}
          </ArticleGrid>
          {articlesByAuthor.length > 6 ? (
            <Button
              as={Link}
              href={createAuthorHref(article.author!)}
              styles={{ mx: 'auto' }}
            >
              Viac článkov{' '}
              {article.author!.gender === Gender.Male ? 'autora' : 'autorky'}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {articlesInCategory.length > 0 ? (
        <Box
          as="section"
          styles={{ display: 'flex', flexDirection: 'column', mb: '2rem' }}
        >
          <Heading
            level={4}
            styles={{ mx: 0, my: 0, px: 0, py: 0, lineHeight: '1.2em' }}
          >
            Prečítajte si tiež v téme {article.categories[0].name}
          </Heading>
          <ArticleGrid>
            {articlesInCategory.slice(0, 6).map((articleInCategory) => (
              <ArticleCard
                compact
                key={articleInCategory.id}
                article={articleInCategory}
              />
            ))}
          </ArticleGrid>
          {articlesInCategory.length > 6 ? (
            <Button
              as={Link}
              href={createCategoryHref(article.categories[0]!)}
              styles={{ mx: 'auto' }}
            >
              Viac článkov v téme
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<
  ArticlePageProps,
  { slug: string }
> = async function getStaticProps({ params }) {
  const result = await graphqlClient.request<
    ArticleQuery,
    ArticleQueryVariables
  >(ArticleDocument, { slug: params!.slug! });

  if (!result.article) {
    return {
      notFound: true,
    };
  }

  const { categoriesWithArticles, content, authorWithArticles, ...article } =
    result.article;

  const articlesInCategory = categoriesWithArticles[0]?.articles ?? [];
  const mdxResult = await serializeMdx(content, { imageLoader });

  return {
    props: {
      categories: result.categories,
      articlesByAuthor: await Promise.all(
        (authorWithArticles || { articles: [] }).articles.map(
          async ({ content: articleCardContent, ...articleCard }) => {
            const res = await serializeMdx(articleCardContent, { imageLoader });

            return {
              ...articleCard,
              timeToRead: res.readTimes,
            };
          },
        ),
      ),
      articlesInCategory: await Promise.all(
        articlesInCategory.map(
          async ({ content: articleCardContent, ...articleCard }) => {
            const res = await serializeMdx(articleCardContent, { imageLoader });

            return {
              ...articleCard,
              timeToRead: res.readTimes,
            };
          },
        ),
      ),
      article: {
        ...article,
        compiledContent: mdxResult,
        timeToRead: mdxResult.readTimes,
      },
    },
  };
};

export const getStaticPaths: GetStaticPaths<{
  categorySlugOrTime: string;
  slug: string;
}> = async function getStaticPaths() {
  const result = await graphqlClient.request<GetAllArticlesQuery>(
    GetAllArticlesDocument,
  );

  return {
    paths: result.articles.map((article) => ({
      params: {
        categorySlugOrTime: Math.floor(
          new Date(article.createdAt).getTime() / 1000,
        ).toString(),
        slug: article.slug,
      },
    })),
    fallback: false,
  };
};
